export interface SocialMediaData {
  platform: string;
  username: string;
  displayName?: string;
  url: string;
  iconName: string;
}

export interface SocialPlatformConfig {
  domains: string[];
  iconName: string;
  extractUsername: (url: string) => string | null;
  displayName: string;
}

const socialPlatforms: Record<string, SocialPlatformConfig> = {
  instagram: {
    domains: ['instagram.com', 'www.instagram.com'],
    iconName: 'instagram',
    displayName: 'Instagram',
    extractUsername: (url: string) => {
      const match = url.match(/instagram\.com\/([^\/\?]+)/);
      return match && match[1] ? match[1] : null;
    },
  },

  discord: {
    domains: [
      'discord.gg',
      'discord.com',
      'discordapp.com',
      'discord.gifts',
      'discord.new',
    ],
    iconName: 'discord',
    displayName: 'Discord',
    extractUsername: (url: string) => {
      // Discord invite links: discord.gg/invite_code
      const inviteMatch = url.match(/discord\.gg\/([^\/\?#]+)/);
      if (inviteMatch && inviteMatch[1]) return inviteMatch[1];

      // User profiles: discord.com/users/user_id
      const userMatch = url.match(/discord\.com\/users\/([^\/\?#]+)/);
      if (userMatch && userMatch[1]) return userMatch[1];

      // Server channels: discord.com/channels/server_id/channel_id
      const channelMatch = url.match(
        /discord\.com\/channels\/([^\/\?#]+)\/([^\/\?#]+)/
      );
      if (channelMatch && channelMatch[1] && channelMatch[2])
        return `${channelMatch[1]}/${channelMatch[2]}`;

      // Application directory: discord.com/application-directory/app_id
      const appMatch = url.match(
        /discord\.com\/application-directory\/([^\/\?#]+)/
      );
      if (appMatch && appMatch[1]) return appMatch[1];

      // Nitro gifts: discord.gifts/gift_code
      const giftMatch = url.match(/discord\.gifts?\/([^\/\?#]+)/);
      if (giftMatch && giftMatch[1]) return giftMatch[1];

      // Templates: discord.new/template_code
      const templateMatch = url.match(/discord\.new\/([^\/\?#]+)/);
      if (templateMatch && templateMatch[1]) return templateMatch[1];

      return null;
    },
  },

  bluesky: {
    domains: ['bsky.app', 'bsky.social'],
    iconName: 'bluesky',
    displayName: 'Bluesky',
    extractUsername: (url: string) => {
      const profileMatch = url.match(
        /bsky\.(?:app|social)\/profile\/([^\/\?#]+)/
      );
      if (profileMatch && profileMatch[1]) {
        return profileMatch[1].replace(/\.bsky\.social$/i, '') || null;
      }
  
      const postMatch = url.match(
        /bsky\.(?:app|social)\/profile\/([^\/\?#]+)\/post\/([^\/\?#]+)/
      );
      if (postMatch) return `${postMatch[1]}/post/${postMatch[2]}`;
  
      const feedMatch = url.match(
        /bsky\.(?:app|social)\/profile\/([^\/\?#]+)\/feed\/([^\/\?#]+)/
      );
      if (feedMatch) return `${feedMatch[1]}/feed/${feedMatch[2]}`;
  
      const listMatch = url.match(
        /bsky\.(?:app|social)\/profile\/([^\/\?#]+)\/lists\/([^\/\?#]+)/
      );
      if (listMatch) return `${listMatch[1]}/lists/${listMatch[2]}`;
  
      const hashtagMatch = url.match(
        /bsky\.(?:app|social)\/hashtag\/([^\/\?#]+)/
      );
      if (hashtagMatch) return `hashtag/${hashtagMatch[1]}`;
  
      const searchMatch = url.match(
        /bsky\.(?:app|social)\/search\?q=([^&\?#]+)/
      );
      if (searchMatch) return `search/${decodeURIComponent(searchMatch[1] as string)}`;
  
      return null;
    },
  },


  twitch: {
    domains: ['twitch.tv', 'www.twitch.tv', 'clips.twitch.tv', 'm.twitch.tv'],
    iconName: 'twitch',
    displayName: 'Twitch',
    extractUsername: (url: string) => {
      // User channels: twitch.tv/username
      const channelMatch = url.match(/twitch\.tv\/([^\/\?#]+)(?:\/)?$/);
      if (
        channelMatch &&
        ![
          'directory',
          'p',
          'videos',
          'clips',
          'collections',
          'events',
          'prime',
        ].includes(channelMatch[1] as string)
      ) {
        return channelMatch[1] as string;
      }

      // Specific videos: twitch.tv/username/video/video_id
      const videoMatch = url.match(
        /twitch\.tv\/([^\/\?#]+)\/video\/([^\/\?#]+)/
      );
      if (videoMatch && videoMatch[1] && videoMatch[2])
        return `${videoMatch[1]}/video/${videoMatch[2]}`;

      // Clips: twitch.tv/username/clip/clip_slug or clips.twitch.tv/clip_slug
      const clipMatch = url.match(
        /(?:twitch\.tv\/([^\/\?#]+)\/clip\/([^\/\?#]+)|clips\.twitch\.tv\/([^\/\?#]+))/
      );
      if (clipMatch && (clipMatch[1] || clipMatch[3])) {
        return clipMatch[3]
          ? `clip/${clipMatch[3]}`
          : `${clipMatch[1]}/clip/${clipMatch[2]}`;
      }

      // Collections: twitch.tv/username/collections/collection_id
      const collectionMatch = url.match(
        /twitch\.tv\/([^\/\?#]+)\/collections\/([^\/\?#]+)/
      );
      if (collectionMatch && collectionMatch[1] && collectionMatch[2])
        return `${collectionMatch[1]}/collection/${collectionMatch[2]}`;

      // Games/Categories: twitch.tv/directory/game/game_name
      const gameMatch = url.match(/twitch\.tv\/directory\/game\/([^\/\?#]+)/);
      if (gameMatch && gameMatch[1])
        return `game/${decodeURIComponent(gameMatch[1])}`;

      // Browse categories: twitch.tv/directory/category/category_name
      const categoryMatch = url.match(
        /twitch\.tv\/directory\/category\/([^\/\?#]+)/
      );
      if (categoryMatch && categoryMatch[1])
        return `category/${decodeURIComponent(categoryMatch[1])}`;

      // Events: twitch.tv/events/event_id
      const eventMatch = url.match(/twitch\.tv\/events\/([^\/\?#]+)/);
      if (eventMatch && eventMatch[1]) return `event/${eventMatch[1]}`;

      // Teams: twitch.tv/team/team_name
      const teamMatch = url.match(/twitch\.tv\/team\/([^\/\?#]+)/);
      if (teamMatch && teamMatch[1]) return `team/${teamMatch[1]}`;

      // VODs/Past broadcasts: twitch.tv/videos/video_id
      const vodMatch = url.match(/twitch\.tv\/videos\/([^\/\?#]+)/);
      if (vodMatch && vodMatch[1]) return `vod/${vodMatch[1]}`;

      return null;
    },
  },

  steam: {
    domains: ['steamcommunity.com', 'store.steampowered.com'],
    iconName: 'steam',
    displayName: 'Steam',
    extractUsername: (url: string): string | null => {
      // User profiles by custom ID: steamcommunity.com/id/username
      const customIdMatch = url.match(/steamcommunity\.com\/id\/([^\/\?#]+)/);
      if (customIdMatch && customIdMatch[1]) return customIdMatch[1] ?? null;

      // User profiles by Steam ID: steamcommunity.com/profiles/steamid64
      const profileMatch = url.match(
        /steamcommunity\.com\/profiles\/([^\/\?#]+)/
      );
      if (profileMatch && profileMatch[1]) return profileMatch[1] ?? null;

      // Games: steamcommunity.com/app/app_id
      const gameMatch = url.match(/steamcommunity\.com\/app\/([^\/\?#]+)/);
      if (gameMatch && gameMatch[1])
        return gameMatch[1] ? `app/${gameMatch[1]}` : null;

      // Groups/Clans: steamcommunity.com/groups/group_name
      const groupMatch = url.match(/steamcommunity\.com\/groups\/([^\/\?#]+)/);
      if (groupMatch && groupMatch[1])
        return groupMatch[1] ? `group/${groupMatch[1]}` : null;

      // Workshops: steamcommunity.com/workshop/browse/?appid=app_id
      const workshopMatch = url.match(
        /steamcommunity\.com\/workshop\/browse\/\?appid=([^&\?#]+)/
      );
      if (workshopMatch && workshopMatch[1])
        return workshopMatch[1] ? `workshop/${workshopMatch[1]}` : null;

      // Workshop items: steamcommunity.com/sharedfiles/filedetails/?id=item_id
      const workshopItemMatch = url.match(
        /steamcommunity\.com\/sharedfiles\/filedetails\/\?id=([^&\?#]+)/
      );
      if (workshopItemMatch && workshopItemMatch[1])
        return workshopItemMatch[1] ? `item/${workshopItemMatch[1]}` : null;

      // Store pages: store.steampowered.com/app/app_id
      const storeMatch = url.match(
        /store\.steampowered\.com\/app\/([^\/\?#]+)/
      );
      if (storeMatch && storeMatch[1])
        return storeMatch[1] ? `store/${storeMatch[1]}` : null;

      // Store bundles: store.steampowered.com/bundle/bundle_id
      const bundleMatch = url.match(
        /store\.steampowered\.com\/bundle\/([^\/\?#]+)/
      );
      if (bundleMatch && bundleMatch[1])
        return bundleMatch[1] ? `bundle/${bundleMatch[1]}` : null;

      // Store publishers: store.steampowered.com/publisher/publisher_name
      const publisherMatch = url.match(
        /store\.steampowered\.com\/publisher\/([^\/\?#]+)/
      );
      if (publisherMatch && publisherMatch[1])
        return publisherMatch[1] ? `publisher/${publisherMatch[1]}` : null;

      // Store developers: store.steampowered.com/developer/developer_name
      const developerMatch = url.match(
        /store\.steampowered\.com\/developer\/([^\/\?#]+)/
      );
      if (developerMatch && developerMatch[1])
        return developerMatch[1] ? `developer/${developerMatch[1]}` : null;

      return null;
    },
  },

  itch: {
    domains: ['itch.io'],
    iconName: 'itch',
    displayName: 'Itch.io',
    extractUsername: (url: string) => {
      const match = url.match(/:\/\/([^\.]+)\.itch\.io(?:\/([^\/\?]+))?/);
      if (!match) return null;

      const username = match[1] ?? null;
      const game = match[2] ?? null;

      return game ? `${game} game` : username;
    },
  },
};

function normalizeUrl(url: string): string {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
}

function getDomainFromUrl(url: string): string {
  try {
    const urlObj = new URL(normalizeUrl(url));
    return urlObj.hostname.toLowerCase();
  } catch {
    return '';
  }
}

function detectSocialPlatform(url: string): SocialPlatformConfig | null {
  const domain = getDomainFromUrl(url);
  
  for (const [platform, config] of Object.entries(socialPlatforms)) {
    if (config.domains.some(d => domain === d || domain.endsWith(`.${d}`))) {
      return config;
    }
  }
  
  return null;
}


export function parseSocialMediaUrl(url: string): SocialMediaData | null {
  if (!url || typeof url !== 'string') {
    return null;
  }

  const normalizedUrl = normalizeUrl(url.trim());
  const platform = detectSocialPlatform(normalizedUrl);
  
  if (!platform) {
    return null;
  }

  const username = platform.extractUsername(normalizedUrl);
  
  if (!username) {
    return null;
  }

  return {
    platform: Object.keys(socialPlatforms).find(
      key => socialPlatforms[key] === platform
    ) || 'unknown',
    username: username,
    displayName: platform.displayName,
    url: normalizedUrl,
    iconName: platform.iconName
  };
}

export function parseSocialMediaUrls(urls: string[]): SocialMediaData[] {
  return urls
    .map(url => parseSocialMediaUrl(url))
    .filter((data): data is SocialMediaData => data !== null);
}

export function isValidSocialMediaUrl(url: string): boolean {
  return parseSocialMediaUrl(url) !== null;
}


export function getSupportedPlatforms(): string[] {
  return Object.keys(socialPlatforms);
}


export function getPlatformConfig(platform: string): SocialPlatformConfig | null {
  return socialPlatforms[platform] || null;
}